// What the BD has reported (TX-US-0028): their own submitted sessions, newest first, read-only.
// A page at a time - the list grows by a session or two a day for as long as the BD works here.
//
// Each update shows what processing made of it (TX-US-0029). One submitted a moment ago is still
// being transcribed, so it is asked about again every few seconds until its summary arrives.

import { useCallback, useEffect, useState } from 'react';

import { getHubSession, listHubSessions } from '../api.js';
import { formatBytes } from '../deliveries/attachments/uploads.js';
import { downloadHubFile } from './HubFiles.jsx';
import { sessionDayLabel, timeLabel } from './localDay.js';
import { REFRESH_EVERY_MS, needsRefresh } from './processing.js';
import RecordingPlayer from './RecordingPlayer.jsx';
import SessionInsight from './SessionInsight.jsx';

// `latest` is the session just submitted, so it appears without the list being read again.
export default function HubHistory({ latest }) {
  const [sessions, setSessions] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback((from) => listHubSessions(from)
    .then((page) => {
      setSessions((shown) => (from ? [...(shown ?? []), ...page.sessions] : page.sessions));
      setCursor(page.has_more ? page.next_cursor : null);
    })
    .catch((err) => setError(err.message)), []);

  useEffect(() => {
    load(null);
  }, [load]);

  useEffect(() => {
    if (!latest) return;
    setSessions((shown) => [
      latest,
      ...(shown ?? []).filter((session) => session.session_id !== latest.session_id),
    ]);
  }, [latest]);

  // The ids still worth asking about, as one string: the effect below restarts when the set
  // changes and not on every render.
  const waiting = (sessions ?? []).filter((session) => needsRefresh(session))
    .map((session) => session.session_id).join(',');

  useEffect(() => {
    if (!waiting) return undefined;
    const timer = setInterval(() => {
      // A failed refresh is not worth a message: the next one is ten seconds away.
      Promise.all(waiting.split(',').map((id) => getHubSession(id).catch(() => null)))
        .then((fresh) => {
          const byId = new Map(fresh.filter(Boolean).map((session) => [session.session_id, session]));
          setSessions((shown) => (shown ?? []).map((session) => byId.get(session.session_id) ?? session));
        });
    }, REFRESH_EVERY_MS);
    return () => clearInterval(timer);
  }, [waiting]);

  return (
    <section className="hub-history" aria-labelledby="hub-history-heading">
      <h2 id="hub-history-heading">Your updates</h2>
      {error && <p className="error">{error}</p>}
      {sessions?.length === 0 && <p className="muted">Nothing submitted yet.</p>}

      <ul className="hub-session-list">
        {(sessions ?? []).map((session) => (
          <li key={session.session_id} className="hub-session">
            <p className="hub-session-when">
              <strong>{sessionDayLabel(session.local_date)}</strong>
              <span>{timeLabel(session.submitted_at)}</span>
            </p>
            {session.text && <p className="hub-session-text">{session.text}</p>}
            <SessionInsight session={session} />
            {session.recording && (
              <RecordingPlayer sessionId={session.session_id} recording={session.recording} />
            )}
            {session.attachments.length > 0 && (
              <ul className="hub-file-list">
                {session.attachments.map((file) => (
                  <li key={file.attachment_id}>
                    <button type="button" className="hub-file-name"
                            onClick={() => downloadHubFile(
                              session.session_id, file.attachment_id, setError,
                            )}
                            aria-label={`Download ${file.filename}`}>
                      <strong>{file.filename}</strong>
                      <small>{formatBytes(file.size_bytes ?? 0)}</small>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>

      {cursor && (
        <button type="button" className="hub-more" onClick={() => load(cursor)}>
          Show earlier updates
        </button>
      )}
    </section>
  );
}
